import http from "@/services/http";
import {
  LoanCreateRequest,
  LoanCreateResponse,
  LoanDetail,
  LoanDelinquency,
  LoanListParams,
  LoanPaymentRequest,
  LoanPaymentResponse,
  LoanListItem,
  CustomerListParams,
  CustomerListItem,
  LoanCalculateRequest,
  PaymentMovementDto,
  PaymentMovementReportDto,
  UpcomingPaymentsReportDto,
  OverdueLoansReportDto,
  PaymentMovementsParams,
  PagedResult,
} from "@/models/loan.model";

export interface LoanListResponse {
  items: LoanListItem[];
  total: number;
}

export interface CustomerListResponse {
  items: CustomerListItem[];
  total: number;
}

class LoanService {
  async listLoans(params: LoanListParams = {}): Promise<LoanListResponse> {
    const query = new URLSearchParams();
    const searchTerm = params.search?.trim();
    if (searchTerm) {
      query.set("search", searchTerm);
      query.set("keyword", searchTerm);
    }
    if (params.status !== null && params.status !== undefined) {
      query.set("status", String(params.status));
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      LoanListItem[] | { items?: LoanListItem[]; total?: number; totalItems?: number }
    >(`/loans${qs ? `?${qs}` : ""}`);

    if (Array.isArray(response)) {
      return { items: response, total: response.length };
    }

    const items = Array.isArray(response?.items) ? response.items : [];
    const totalFromResponse =
      typeof response?.total === "number"
        ? response.total
        : typeof response?.totalItems === "number"
          ? response.totalItems
          : undefined;
    const total = totalFromResponse ?? items.length;
    return { items, total };
  }

  async listCustomers(params: CustomerListParams = {}): Promise<CustomerListResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      CustomerListItem[] | { items?: CustomerListItem[]; total?: number; totalItems?: number }
    >(`/loans/customers${qs ? `?${qs}` : ""}`);

    if (Array.isArray(response)) {
      return { items: response, total: response.length };
    }

    const items = Array.isArray(response?.items) ? response.items : [];
    const totalFromResponse =
      typeof response?.total === "number"
        ? response.total
        : typeof response?.totalItems === "number"
          ? response.totalItems
          : undefined;
    const total = totalFromResponse ?? items.length;
    return { items, total };
  }

  async calculateLoan(data: LoanCalculateRequest): Promise<LoanCreateResponse> {
    return http.post<LoanCreateResponse>("/loans/calculate", data);
  }

  async createLoan(data: LoanCreateRequest): Promise<LoanCreateResponse> {
    return http.post<LoanCreateResponse>("/loans", data);
  }

  async getLoanById(loanId: string): Promise<LoanDetail> {
    return http.get<LoanDetail>(`/loans/${loanId}`);
  }

  async assignClientUser(loanId: string, clientUserId: string | null): Promise<void> {
    await http.put<void>(`/loans/${loanId}/client`, { clientUserId });
  }

  async deleteLoan(loanId: string): Promise<void> {
    await http.delete<void>(`/loans/${loanId}`);
  }

  async closeLoan(loanId: string, reason: string): Promise<void> {
    await http.post<void>(`/loans/${loanId}/close`, { reason });
  }

  async addPayment(loanId: string, payload: LoanPaymentRequest): Promise<LoanPaymentResponse> {
    return http.post<LoanPaymentResponse>(`/loans/${loanId}/payments`, payload);
  }

  async updatePayment(loanId: string, paymentId: string, payload: LoanPaymentRequest): Promise<LoanPaymentResponse> {
    return http.put<LoanPaymentResponse>(`/loans/${loanId}/payments/${paymentId}`, payload);
  }

  async getDelinquency(loanId: string, asOf: string): Promise<LoanDelinquency> {
    const qs = new URLSearchParams();
    qs.set("asOf", asOf);
    return http.get<LoanDelinquency>(`/loans/${loanId}/delinquency?${qs.toString()}`);
  }

  async getPaymentMovements(loanId: string, paymentId: string): Promise<PaymentMovementDto[]> {
    return http.get<PaymentMovementDto[]>(`/loans/${loanId}/payments/${paymentId}/movements`);
  }

  async getLoanMovements(loanId: string, params: PaymentMovementsParams = {}): Promise<PagedResult<PaymentMovementDto>> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();
    return http.get<PagedResult<PaymentMovementDto>>(`/loans/${loanId}/movements${qs ? `?${qs}` : ""}`);
  }

  async getMovementsReport(params: { fromDate?: string; toDate?: string; dealerId?: string; garageId?: string }): Promise<PaymentMovementReportDto> {
    const query = new URLSearchParams();
    if (params.fromDate) query.set("fromDate", params.fromDate);
    if (params.toDate) query.set("toDate", params.toDate);
    if (params.dealerId) query.set("dealerId", params.dealerId);
    if (params.garageId) query.set("garageId", params.garageId);
    const qs = query.toString();
    return http.get<PaymentMovementReportDto>(`/loans/movements/report${qs ? `?${qs}` : ""}`);
  }

  async getUpcomingPayments(params: { daysAhead?: number; dealerId?: string; garageId?: string } = {}): Promise<UpcomingPaymentsReportDto> {
    const query = new URLSearchParams();
    if (params.daysAhead !== undefined && params.daysAhead !== null) query.set("daysAhead", String(params.daysAhead));
    if (params.dealerId) query.set("dealerId", params.dealerId);
    if (params.garageId) query.set("garageId", params.garageId);
    const qs = query.toString();
    return http.get<UpcomingPaymentsReportDto>(`/loans/upcoming-payments${qs ? `?${qs}` : ""}`);
  }

  async getOverdueReport(params: { asOf?: string; garageId?: string } = {}): Promise<OverdueLoansReportDto> {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    if (params.garageId) query.set("garageId", params.garageId);
    const qs = query.toString();
    return http.get<OverdueLoansReportDto>(`/loans/overdue-report${qs ? `?${qs}` : ""}`);
  }
}

export default new LoanService();
